import { hideVisually, size } from "polished";
import * as React from "react";
import styled, { css } from "styled-components";
import ArrowDown from "../img/icons/arrow-down.svg";
import theme from "../theme";
import { isRTL } from "../utils/locale";

export const FieldWrap = styled.div`
  margin-bottom: ${theme.space[4]};
  position: relative;
  z-index: 1;
`;

const fieldStyles = {
  display: "block",
  width: "100%",
  padding: theme.space[2],
  backgroundColor: theme.colors.white4,
  border: theme.borders[0],
  borderRadius: "8px",
  color: theme.colors.white,
  fontFamily: theme.fonts.book,
  fontSize: theme.fontSizes[3],
  letterSpacing: "-0.8px",

  ":hover, :focus": {
    borderColor: `${theme.colors.blueDark}`,
  },
};

interface ILabelProps {
  $labelVisibility?: boolean;
}

const FieldLabel = styled.label<ILabelProps>`
  display: block;
  margin-bottom: ${theme.space[1]};
  color: ${theme.colors.white};
  font-family: ${theme.fonts.impact};
  font-size: ${theme.fontSizes[0]};

  cursor: pointer;

  ${({ $labelVisibility }) => $labelVisibility === false && hideVisually};
`;

const Hint = styled.span`
  margin-bottom: ${theme.space[1]};
  display: block;
  color: ${theme.colors.white};
  font-weight: 100;
  font-size: 14px;
`;

interface InputProps {
  hasErrors?: boolean;
  required?: boolean;
}

const Input = styled.input<InputProps>`
  ${fieldStyles}
  ${(props) =>
    props.hasErrors &&
    css`
      border-color: ${theme.colors.error};

      &:focus {
        border-color: ${theme.colors.error};
      }
    `}
    box-sizing: border-box;
`;

const SelectWrap = styled.div`
  position: relative;
  z-index: ${theme.zIndex.select.wrap};
  border: none;
  border-radius: ${theme.radii[0]};
  background-color: ${theme.colors.white4};

  &:hover {
    border-color: ${theme.colors.blueDark};
  }

  &::after {
    z-index: ${theme.zIndex.select.select};
    content: "";
    background: url(${ArrowDown}) no-repeat;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 14px;
    height: 8px;
    background-size: contain;
    ${
      isRTL()
        ? css`
            left: ${(props) => props.theme.space[2]};
          `
        : css`
            right: ${(props) => props.theme.space[2]};
          `
    };
  }
`;

// Unable to extend SelectWrap for some reason
const InlineSelectWrap = styled.div`
  position: relative;
  display: inline-block;
  z-index: ${theme.zIndex.select.wrap};
  border: none;
  border-radius: 8px;
  &:hover {
    border-color: ${theme.colors.blueDark};
  }
  &::after {
    content: "";
    background: url(${ArrowDown}) no-repeat;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 14px;
    height: 8px;
    background-size: contain;
    ${
      isRTL()
        ? css`
            left: ${(props) => props.theme.space[2]};
          `
        : css`
            right: ${(props) => props.theme.space[2]};
          `
    };
  }
`;

const Select = styled.select`
  ${fieldStyles}
  position: relative;
  z-index: ${theme.zIndex.select.select};
  border: ${theme.borders[2]};
  border-radius: 8px;
  background-color: ${theme.colors.whiteGrey};
  color: ${theme.colors.primary};
  appearance: none;
  cursor: pointer;

  &::-ms-expand {
    display: none; /* [1] */
  }
`;

const InlineSelect = styled(Select)`
  display: inline-block;
  width: auto;
  padding-inline-end: 40px;
`;

const SearchWrap = styled.div`
  position: relative;
`;

const SearchIcon = styled.span`
  ${size("17px")}
  content: "";
  background-size: contain;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-repeat: no-repeat;
  background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17"><path fill="%23ffffff" d="M16.789,15.763 L12.064,10.994 C14.2876709,8.1921368 13.9322904,4.140569 11.254801,1.76860777 C8.57731153,-0.603353465 4.5124528,-0.467631625 1.99914537,2.07764547 C-0.514162068,4.62292257 -0.598496252,8.68917203 1.807117,11.3364678 C4.21273026,13.9837635 8.26846617,14.2879068 11.042,12.029 L15.736,16.767 C16.0121708,17.0570934 16.470954,17.0691666 16.762,16.794 C17.052681,16.5160968 17.064737,16.0557367 16.789,15.763 L16.789,15.763 Z M6.773,12.0860194 C4.23641324,12.0857011 2.05326977,10.2936268 1.5586779,7.80572583 C1.06408604,5.31782484 2.39579448,2.82700201 4.73939269,1.85652796 C7.08299091,0.886053911 9.78576378,1.7062152 11.194822,3.81543958 C12.6038802,5.92466396 12.3267871,8.73551172 10.533,10.529 C9.53781123,11.5291874 8.18394325,12.0898183 6.773,12.0860194 Z"/></svg>');
  ${
    isRTL()
      ? css`
          left: ${(props) => props.theme.space[2]};
        `
      : css`
          right: ${(props) => props.theme.space[2]};
        `
  };
`;

const SearchInput = styled(Input)`
  /* background-color: ${({ theme }) => theme.colors.white4}; */
  /* stylelint-disable property-no-vendor-prefix */
  -webkit-appearance: textfield;
  appearance: textfield;

  &::placeholder {
    color: ${({ theme }) => theme.colors.grey};
  }

  &:focus + ${SearchIcon} {
    display: none;
  }
`;

export const FieldCols = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    display: flex;
  }
`;

export const FieldCol = styled.div`
  padding-inline-end: ${({ theme }) => theme.space[2]};
  padding-inline-start: ${({ theme }) => theme.space[2]};

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    flex: 1;
  }
`;

interface ILabelHintProps {
  hint?: React.ReactNode;
  id: string;
  label: React.ReactNode;
  required?: boolean;
  labelVisibility?: boolean;
}

interface IFieldProps extends ILabelHintProps {
  name?: string;
  value?: string | number;
}

interface IInputProps extends IFieldProps {
  maxLength?: number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  disabled?: boolean;
  readonly?: boolean;
  hasErrors?: boolean;
  required?: boolean;
}

const LabelHint: React.FC<ILabelHintProps> = ({
  id,
  label,
  hint,
  required = true,
  labelVisibility = true,
}) => (
  <FieldLabel htmlFor={id} $labelVisibility={labelVisibility}>
    <span>{`${label}${required ? "*" : ""}`}</span>
    {hint && <Hint>{hint}</Hint>}
  </FieldLabel>
);

const InlineLabel: React.FC<ILabelHintProps> = ({
  id,
  label,
  required = true,
  labelVisibility = true,
}) => (
  <FieldLabel htmlFor={id} $labelVisibility={labelVisibility}>
    <span>{`${label}${required && "*"}`}</span>
  </FieldLabel>
);

export const InputField: React.FC<IInputProps> = ({
  hint,
  id,
  label,
  maxLength,
  name,
  onBlur,
  onChange,
  type,
  value,
  required = true,
  hasErrors,
  disabled = false,
  readonly = false,
  labelVisibility = true,
}) => (
  <>
    <LabelHint
      id={id}
      label={label}
      hint={hint}
      required={required}
      labelVisibility={labelVisibility}
    />
    <Input
      required={required}
      type={type}
      id={id}
      name={name}
      onChange={onChange}
      value={value}
      onBlur={onBlur}
      maxLength={maxLength}
      disabled={disabled}
      readOnly={readonly}
      hasErrors={hasErrors}
    />
  </>
);

interface ISearchProps extends IFieldProps {
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
}

export const SearchField: React.FC<ISearchProps> = ({
  hint,
  id,
  label,
  labelVisibility = true,
  name,
  onChange,
  placeholder,
  required = true,
  value,
  ...rest
}) => (
  <>
    <LabelHint
      id={id}
      label={label}
      labelVisibility={labelVisibility}
      hint={hint}
      required={required}
    />
    <SearchWrap>
      <SearchInput
        type="search"
        id={id}
        name={name}
        onChange={onChange}
        placeholder={placeholder}
        value={value}
        {...rest}
      />
      <SearchIcon />
    </SearchWrap>
  </>
);

interface INumberProps extends IFieldProps {
  ariaValueMax?: number;
  ariaValueMin?: number;
  ariaValueNow?: number;
  max?: string | number;
  min?: string | number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  step?: string | number;
}

export const NumberField: React.FC<INumberProps> = ({
  ariaValueMax,
  ariaValueMin,
  ariaValueNow,
  hint,
  id,
  label,
  max = "",
  min = "",
  name,
  onChange,
  step = "",
  value,
  required = true,
}) => (
  <>
    <LabelHint id={id} label={label} hint={hint} required={required} />
    <Input
      required={required}
      aria-valuemax={ariaValueMax}
      aria-valuemin={ariaValueMin}
      aria-valuenow={ariaValueNow}
      type="number"
      id={id}
      max={max}
      min={min}
      name={name}
      onChange={onChange}
      step={step}
      value={value}
    />
  </>
);

const Checkbox = styled.input`
  ${hideVisually()};

  &:hover + span::before {
    border-color: ${(props) => props.theme.borders[0]};
    border-radius: ${(props) => props.theme.radii[1]};
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="75" height="75"><path d="M28.051 68.625c-1.199 0-2.324-.449-3.149-1.273L5.254 47.102c-1.875-1.727-1.949-4.648-.149-6.523 1.727-1.875 4.649-1.949 6.524-.148l14.398 10.273c.825.75 2.102.602 2.774-.301L62.926 8.25c1.5-2.023 4.426-2.477 6.449-.898 2.023 1.5 2.477 4.425.898 6.449L31.727 66.75c-.825 1.051-2.024 1.727-3.301 1.801-.153.074-.227.074-.375.074z" fill="%23FFFFFF"/></svg>');
  }

  &:focus + span {
    outline: -webkit-focus-ring-color auto 5px;
  }

  &:checked + span::before {
    background-color: ${(props) => props.theme.colors.white4};
    border-radius: ${(props) => props.theme.radii[1]};
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="75" height="75"><path d="M28.051 68.625c-1.199 0-2.324-.449-3.149-1.273L5.254 47.102c-1.875-1.727-1.949-4.648-.149-6.523 1.727-1.875 4.649-1.949 6.524-.148l14.398 10.273c.825.75 2.102.602 2.774-.301L62.926 8.25c1.5-2.023 4.426-2.477 6.449-.898 2.023 1.5 2.477 4.425.898 6.449L31.727 66.75c-.825 1.051-2.024 1.727-3.301 1.801-.153.074-.227.074-.375.074z" fill="white" /></svg>');
  }
`;

interface ICheckboxText {
  hasLogo?: boolean;
}

const CheckboxText = styled.span<ICheckboxText>`
  display: inline-block;
  position: relative;
  line-height: 1.6;
  ${
    isRTL()
      ? css`
          padding: 6px 42px 6px 0;
        `
      : css`
          padding: 6px 0 6px 42px;
        `
  };

  &::before {
    content: "";
    position: absolute;
    top: 50%;
    width: 30px;
    height: 30px;
    transform: translateY(-50%);
    border: ${theme.borders[0]};
    border-radius: ${(props) => props.theme.radii[1]};
    background-repeat: no-repeat;
    background-size: contain;
    vertical-align: middle;
    ${
      isRTL()
        ? css`
            right: 0;
          `
        : css`
            left: 0;
          `
    };
  }

  ${({ hasLogo }) =>
    hasLogo &&
    css`
      display: flex;
      align-items: center;
    `}
`;

interface ICheckboxProps {
  checked?: boolean;
  id: string;
  label: React.ReactNode;
  name?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  logo?: any;
  hideLabel?: boolean;
}

const LogoImg = styled.img`
  height: 29px;
  width: auto;
  max-width: 100%;
`;

export const CheckboxField: React.FC<ICheckboxProps> = ({
  id,
  onChange,
  label,
  checked = false,
  logo,
  hideLabel = false,
}) => {
  let labelStyles = {};
  if (hideLabel) {
    labelStyles = hideVisually;
  }

  return (
    <div>
      <label htmlFor={id}>
        <Checkbox
          type="checkbox"
          id={id}
          role="checkbox"
          checked={checked}
          aria-checked={checked}
          onChange={onChange}
        />
        <CheckboxText hasLogo={logo}>
          {hideLabel === false && <span style={labelStyles}>{label}</span>}
          {logo && <LogoImg src={logo} />}
        </CheckboxText>
      </label>
    </div>
  );
};

const Fieldset = styled.fieldset`
  padding: 0;
  border: 0;
`;

interface ILabelLegendProps {
  children: React.ReactNode;
  legend: string;
}

export const LabelLegend: React.FC<ILabelLegendProps> = ({
  children,
  legend,
}) => (
  <Fieldset>
    <legend>
      <span>{legend}</span>
    </legend>
    {children}
  </Fieldset>
);

const Radio = styled.input`
  ${hideVisually()};

  &:hover + span::before {
    background-color: #e5e5e5;
    box-shadow: 0 0 0 0.2rem ${(props) => props.theme.colors.blueDark};
  }

  &:focus + span {
    outline: -webkit-focus-ring-color auto 5px;
  }

  &:checked + span::before {
    box-shadow: 0 0 0 0.2rem ${(props) => props.theme.colors.blueDark};
    background-color: ${(props) => props.theme.colors.blue};
  }
`;

const RadioText = styled.span`
  display: inline-block;
  position: relative;
  line-height: 1.6;
  ${
    isRTL()
      ? css`
          padding: 6px 42px 6px 0;
        `
      : css`
          padding: 6px 0 6px 42px;
        `
  };

  &::before {
    /* [1] */
    display: inline-block;
    position: absolute;
    top: 50%;
    width: 23px;
    height: 23px;
    transform: translateY(-50%);
    border: 5px solid white;
    border-radius: 50%;
    box-shadow: 0 0 0 2px #e5e5e5;
    content: "";
    vertical-align: middle;
    ${
      isRTL()
        ? css`
            right: 0;
          `
        : css`
            left: 0;
          `
    };
  }
`;

const StyledFieldFeedback = styled.p`
  color: ${theme.colors.error};
  margin-top: 0;
`;

interface IOptions {
  checked?: boolean;
  id: string;
  label: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  value: string;
}
interface IRadioGroupProps {
  defaultOptionIndex?: number;
  legend: React.ReactNode;
  name: string;
  options: IOptions[];
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
}

export const RadioGroup: React.FC<IRadioGroupProps> = ({
  defaultOptionIndex,
  legend,
  name,
  options,
  onChange,
  required = true,
}) => (
  <Fieldset>
    <legend>
      <FieldLabel>
        {legend}
        {required ? "*" : ""}
      </FieldLabel>
    </legend>
    {options.map((o, index) => (
      <div key={o.id}>
        <label htmlFor={o.id}>
          <Radio
            type="radio"
            id={o.id}
            name={name}
            value={o.value}
            role="radio"
            aria-checked={o.checked}
            onChange={onChange}
            checked={o.checked}
            defaultChecked={index === defaultOptionIndex}
          />
          <RadioText>{o.label}</RadioText>
        </label>
      </div>
    ))}
  </Fieldset>
);

interface ISelectProps extends IFieldProps {
  children: React.ReactNode;
  onBlur?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  required?: boolean;
  labelVisibility?: boolean;
}

export const SelectField: React.FC<ISelectProps> = ({
  children,
  hint,
  id,
  label,
  onBlur,
  onChange,
  value,
  labelVisibility = true,
  required = true,
}) => (
  <>
    <LabelHint
      id={id}
      label={label}
      hint={hint}
      required={required}
      labelVisibility={labelVisibility}
    />
    <SelectWrap>
      <Select id={id} onBlur={onBlur} onChange={onChange} value={value}>
        {children}
      </Select>
    </SelectWrap>
  </>
);

export const InlineSelectField: React.FC<ISelectProps> = ({
  children,
  id,
  label,
  onChange,
  value,
}) => (
  <>
    <InlineLabel id={id} label={label} />
    <InlineSelectWrap>
      <InlineSelect id={id} onChange={onChange} value={value}>
        {children}
      </InlineSelect>
    </InlineSelectWrap>
  </>
);

interface IFieldFeedback {
  children: React.ReactNode;
}

export const FieldFeedback: React.FC<IFieldFeedback> = ({ children }) => (
  <StyledFieldFeedback>{children}</StyledFieldFeedback>
);
